/**
 * Peripheral Device Detection Utility
 * Detects external cameras and displays
 */

import { isMobileDevice } from "./deviceDetection.util";
import { isVirtualCamera } from "./camera.util";

export const DEVICE_TYPES = {
  EXTERNAL_CAMERA: "external_camera",
  EXTERNAL_DISPLAY: "external_display",
};

/**
 * Detect all peripheral devices (cameras and displays)
 * Logic: If more than allowed cameras/displays, flag as external peripheral
 * @returns {Promise<Array>} - Array of detected violating devices
 */
export const detectAllPeripheralDevices = async () => {
  const devices = [];
  const isMobile = isMobileDevice();

  let totalCameras = 0;
  let totalDisplays = 0;

  // 1. Count Cameras via MediaDevices API (filter out virtual cameras)
  // IMPORTANT: Skip camera detection on mobile devices entirely
  // Mobile devices have built-in cameras that are part of the device, not external peripherals
  if (!isMobile) {
    if (
      navigator.mediaDevices &&
      typeof navigator.mediaDevices.enumerateDevices === "function"
    ) {
      try {
        const mediaDevices = await navigator.mediaDevices.enumerateDevices();

        // Filter out virtual cameras before counting
        const realCameras = mediaDevices.filter(
          (device) => device.kind === "videoinput" && !isVirtualCamera(device),
        );

        // Count external cameras specifically based on label keywords if possible,
        // otherwise rely on total count > 1 logic but slightly relaxed
        const externalCameraKeywords = ["usb", "external", "webcam"];
        // Note: 'webcam' is common in both built-in and external, so context matters.
        // Better approach: Count cameras that are NOT clearly internal

        const clearlyInternal = [
          "internal",
          "integrated",
          "built-in",
          "facetime",
        ];

        // Count confirmed external devices
        let externalCount = 0;
        let virtualCount = 0;

        realCameras.forEach((cam) => {
          const label = cam.label.toLowerCase();

          if (label.includes("virtual")) {
            virtualCount++;
            return; // Skip virtual cameras like Windows Virtual Camera
          }

          const isInternal = clearlyInternal.some((k) => label.includes(k));
          if (!isInternal) {
            // If it's not clearly internal, treat as potentially external
            // But only if we have > 1 total camera (otherwise single camera is always allowed)
            externalCount++;
          }
        });

        const physicalCams = realCameras.length - virtualCount;

        // If we have > 1 physical camera, and at least one seems properly external, flag it.
        if (physicalCams > 1 && externalCount > 0) {
          totalCameras = physicalCams;
        } else {
          // Treat as 1 valid camera if only 1 exists or if multiple are all internal system cameras
          totalCameras = 1;
        }
      } catch (error) {
        console.log("❌ MediaDevices API error:", error.message);
      }
    } else {
      console.log(
        "⚠️ MediaDevices API or enumerateDevices not available - cannot detect cameras",
      );
    }
  } else {
    // Mobile devices: Skip camera detection entirely
    // Mobile cameras are built-in and not considered external peripherals
    totalCameras = 0; // Set to 0 so it won't trigger any flags
    console.log(
      "📱 Mobile device detected - skipping camera detection (built-in cameras are not external peripherals)",
    );
  }

  // 2. Count Displays via Screen API
  // Skip display detection on mobile devices - they only have one display
  if (!isMobile) {
    totalDisplays = 1; // Default assumes 1 display (laptop/desktop monitor)
    try {
      // Method 1: Try Window Management API (most accurate)
      if ("getScreenDetails" in window) {
        try {
          // Check permissions first or wrapped in try/catch as it might prompt
          const screenDetails = await window.getScreenDetails().catch((e) => {
            console.log("getScreenDetails permission/error:", e);
            return null;
          });

          if (
            screenDetails &&
            screenDetails.screens &&
            screenDetails.screens.length > 0
          ) {
            totalDisplays = screenDetails.screens.length;
          }
        } catch (error) {
          console.log("Screen Details API not available:", error.message);
        }
      }

      // Method 2: Fallback to simple screen detection if API failed or wasn't used
      // Only verify "isExtended" property if we haven't confirmed count yet
      if (totalDisplays === 1) {
        // Still default
        // Check if extended desktop is detected
        if (window.screen && typeof window.screen.isExtended === "boolean") {
          if (window.screen.isExtended) {
            totalDisplays = 2; // At least 2 if extended
          }
        }
      }
    } catch (error) {
      console.log("❌ Display detection error:", error.message);
      totalDisplays = 1; // Default fallback on error
    }
  } else {
    // Mobile devices always have exactly 1 display - skip detection
    totalDisplays = 1;
  }

  // Flag devices if count exceeds allowed
  if (!isMobile && totalCameras > 1) {
    devices.push({
      type: DEVICE_TYPES.EXTERNAL_CAMERA,
      name: "External Camera Detected",
      details: `${totalCameras} cameras detected (expected 1 built-in camera)`,
      severity: "high",
      reason: "Multiple cameras detected - please disconnect external cameras",
    });
  }

  // Displays: Flag if > 1 (only check on desktop, mobile always has 1 display)
  if (!isMobile && totalDisplays > 1) {
    devices.push({
      type: DEVICE_TYPES.EXTERNAL_DISPLAY,
      name: "External Display Detected",
      details: `${totalDisplays} displays detected (expected 1)`,
      severity: "high",
      reason: "Multiple displays detected",
    });
  }

  return devices;
};

/**
 * Request Window Management API permissions (needed for display detection)
 * @returns {Promise<boolean>} - True if permission granted
 */
export const requestDisplayPermissions = async () => {
  if (!("getScreenDetails" in window)) {
    console.log("❌ Window Management API not available");
    return false;
  }

  try {
    await window.getScreenDetails();
    return true;
  } catch (error) {
    console.log("❌ Display permission denied:", error.message);
    return false;
  }
};

/**
 * Request media device permissions (needed for camera detection)
 * @returns {Promise<boolean>} - True if permission granted
 */
export const requestMediaPermissions = async () => {
  if (!navigator.mediaDevices) {
    console.log("❌ MediaDevices API not available");
    return false;
  }

  try {
    // Request temporary stream to get permissions (only video needed for camera detection)
    const stream = await navigator.mediaDevices.getUserMedia({
      video: true,
    });
    // Stop the stream immediately
    stream.getTracks().forEach((track) => track.stop());
    return true;
  } catch (error) {
    console.log("❌ Media permission denied:", error.message);
    return false;
  }
};

/**
 * Request all required permissions for peripheral device detection
 * Only requests display and media permissions (for camera and display detection)
 * On mobile: Only request display permissions (camera detection is skipped)
 * @returns {Promise<Object>} - Permission results
 */
export const requestAllPermissions = async () => {
  const isMobile = isMobileDevice();

  const results = {
    display: false,
    media: false,
  };

  try {
    // Request display permissions (for multiple displays) - only on desktop
    if (!isMobile) {
      results.display = await requestDisplayPermissions();
    }

    // Request media permissions (for camera detection) - only on desktop
    // On mobile, skip since we don't detect cameras as external peripherals
    if (!isMobile) {
      results.media = await requestMediaPermissions();
    } else {
      console.log(
        "📱 Mobile: Skipping camera permission request (cameras are built-in, not external)",
      );
    }
  } catch (error) {
    console.error("❌ Error requesting permissions:", error);
  }

  // On mobile, we don't need permissions for peripheral detection
  // (cameras are built-in, displays are always 1)
  const hasAnyPermission = isMobile
    ? true
    : Object.values(results).some((granted) => granted);

  return { results, hasAnyPermission };
};
